import type { useRouter } from 'expo-router';

/** El tipo que devuelve `useRouter()`. expo-router no exporta `Router`. */
type Enrutador = ReturnType<typeof useRouter>;

/**
 * Vuelve a la pantalla anterior, y si no hay ninguna, va a una ruta segura.
 *
 * POR QUÉ EXISTE
 * `router.back()` a secas no hace NADA cuando la pila de navegación está
 * vacía. En esta app eso pasa constantemente: el embudo navega con
 * `router.replace` (que borra el historial) y además se entra a pantallas
 * sueltas desde una notificación push o un enlace. Resultado: la flecha
 * "Atrás" del header se toca y no pasa nada.
 *
 * Ese es exactamente el motivo de rechazo vc13 de Google Play — "Unresponsive
 * UI elements, such as buttons or icons". Un revisor que entra por una
 * notificación y toca la flecha ve una app trabada.
 *
 * @param router  el objeto de `useRouter()`
 * @param destino a dónde ir si no hay historial. Por defecto la raíz, que
 *                siempre existe y re-enruta según el rol.
 */
export function volver(router: Enrutador, destino: string = '/') {
  if (router.canGoBack()) {
    router.back();
    return;
  }
  router.replace(destino as any);
}

/**
 * ENTRA A LA APP SIN APILAR UN SEGUNDO MENÚ.
 *
 * EL FALLO QUE CIERRA. Las pantallas del embudo (kyc-welcome, kyc-documents)
 * viven en el stack RAÍZ, encima de las pestañas. Salir de ahí con
 * `router.replace('/(tabs)/map')` NO reemplaza las pestañas de abajo: sustituye
 * la pantalla del embudo por una SEGUNDA copia del navegador de pestañas,
 * encima de la primera. Dos barras de menú, dos mapas y dos juegos de
 * escuchadores del mismo viaje. Es lo que el fundador describió como «al
 * intentar salir varias veces rompe el menú».
 *
 * El propio proyecto ya lo tenía medido y escrito en `src/app/_layout.tsx`
 * (ver la nota larga sobre `dismissTo` y el caso V52): con las pestañas ya
 * montadas debajo hay que CERRAR lo que está encima (`dismissTo`), no
 * reemplazarlo.
 *
 * Aquí NO sirve `volver()`: «Ahora no, entrar a la app» tiene que ENTRAR, y un
 * `back()` devolvería a la pantalla anterior — que es justo de donde la persona
 * está intentando salir.
 *
 * `dismissTo` solo existe en expo-router recientes y solo funciona si hay algo
 * del stack raíz encima de las pestañas; si no está o falla, `replace` es lo
 * correcto, porque entonces no hay pestañas debajo que duplicar.
 */
export function entrarALaApp(router: Enrutador, destino: string) {
  const ir: any = router as any;
  if (typeof ir.dismissTo === 'function') {
    try {
      ir.dismissTo(destino);
      return;
    } catch {
      // Sin pestañas debajo `dismissTo` no tiene a dónde volver: se sigue por
      // el camino de siempre.
    }
  }
  router.replace(destino as any);
}
